<script setup>
import { computed } from 'vue'
import { LIBRARY_ROUTES } from '@utils/library-routes'

const props = defineProps({
    title: { type: String, required: true },
    count: { type: Number, default: null },
    countLabel: { type: String, default: 'mixes' },   // 'mixes' | 'artists' | 'genres' | 'shows'
    searchQuery: { type: String, default: '' },
})

const emit = defineEmits(['update:search-query'])

function singularize(label) {
    if (label.endsWith('xes')) return label.slice(0, -2)   // mixes -> mix
    if (label.endsWith('s')) return label.slice(0, -1)     // artists -> artist, genres -> genre, shows -> show
    return label
}

const countText = computed(() => {
    if (props.count === null) return ''
    const label = props.count === 1 ? singularize(props.countLabel) : props.countLabel
    return `${props.count} ${label}`
})
</script>

<template>
    <header class="header">
        <div class="title-row">
            <h1 class="title">{{ title }}</h1>
            <span
                v-if="countText"
                class="count"
            >{{ countText }}</span>
            <RouterLink
                class="search-link"
                :to="{ name: LIBRARY_ROUTES.search }"
                aria-label="Filter Your Library"
            >
                <BaseIcon
                    class="search-icon"
                    name="search"
                />
            </RouterLink>
            <div class="search-field">
                <BaseSearchInput
                    :value="searchQuery"
                    placeholder="Filter Your Library"
                    variant="filter"
                    :debounce-delay="0"
                    @input="emit('update:search-query', $event)"
                />
                <!-- @input is the per-keystroke emit — the query lives in the PARENT, which
                     renders inline results (no navigation, debounce-delay 0: results are
                     instant client-side computeds). :value keeps the field CONTROLLED:
                     without it, BaseSearchInput's internal localValue reads a never-updated
                     default and Vue force-resets the DOM value on the next re-render. -->
            </div>
        </div>
    </header>
</template>

<style lang="scss" scoped>
@use '@design' as *;

.header {
    display: flex;
    flex-direction: column;
    gap: $s-s;
    margin-bottom: $s-l;

    // match the browse pages' desktop rhythm (28px below the top bar,
    // ~60px title-to-tabs — measured against /browse/mixes/latest)
    @include desktop {
        margin-top: $s-s;
        margin-bottom: $s-h;
    }
}

.title-row {
    display: flex;
    gap: $s-s;
    align-items: baseline;
}

.title {
    @include pageTitle;

    margin: 0;
}

.count {
    font-size: 14px;
    color: $c-text-faded;
}

.search-link {
    margin-left: auto;
    text-decoration: none;

    &:hover,
    &:active {
        text-decoration: none;
    }

    &:focus-visible {
        outline: 2px solid $c-highlight;
    }

    @include desktop {
        display: none;
    }
}

.search-icon {
    width: 1.4rem;
    height: 1.4rem;
    fill: $c-white;
}

.search-field {
    display: none;

    // unboxed bar with a bottom rule (Figma 897-550): no fill, no surround — the
    // boxed look belongs to the GLOBAL search; the library filter is an underlined bar
    @include desktop {
        display: block;
        min-width: 24rem;
        padding: $s-xxs $s-s $s-s;
        margin-left: auto;
        border-bottom: 1px solid $c-boulder-grey;
    }

}
</style>
